import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import useLocalStorage from '../hooks/useLocalStorage';

import { AuthContext, LOCAL_STORAGE_SESSION_KEY } from './AuthContext';

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [hasActiveSession, setHasActiveSession] = useLocalStorage(LOCAL_STORAGE_SESSION_KEY, false);

  useSWR(() => (hasActiveSession ? `/auth/user` : null), null, {
    revalidateOnFocus: true,
    refreshInterval: 300000, // 5min in ms (5*60*100)
    refreshWhenHidden: true, // refresh when window is not focused
    shouldRetryOnError: false,
    onError: (err) => {
      if (err.response.status === 401 && err.response.data.name === 'Unauthorized') {
        setHasActiveSession(false);
      }
    },
  });

  useEffect(() => {
    if (!hasActiveSession) {
      navigate('/login');
    }
  }, [hasActiveSession, navigate]);

  const login = useCallback(() => {
    setHasActiveSession(true);
  }, [setHasActiveSession]);

  const logout = useCallback(() => {
    setHasActiveSession(false);
    navigate('/api/logout', { replace: true });
  }, [navigate, setHasActiveSession]);

  const value = useMemo(
    () => ({
      hasActiveSession,
      login,
      logout,
    }),
    [login, logout, hasActiveSession]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
